<template>
    <table class="table table-striped table-bordered table-sm table-projects">
        <tbody>
            <tr>
                <td class="prop-title">{{ trans('project_types.label') }}</td>
                <td>{{ project.content.type }}</td>
            </tr>
            <tr>
                <td class="prop-title">{{ trans('funding.label') }}</td>
                <td>
                    <ul class="list-unstyled inst-financing">
                        <li v-for="financier in institutionsAs('financier')" :key="financier.id">
                            <em>{{ institutionLabel(financier) }}</em>
                        </li>
                        <li v-if="institutionsAs('financier').length == 0">‒</li>
                    </ul>
                </td>
            </tr>
            <tr v-if="tagging.methods.length > 0">
                <td class="prop-title">{{ trans('methods.label') }}</td>
                <td>
                    <ul class="list-ssv">
                        <li v-for="cat in tagging.methods" :key="cat.id">
                            {{ cat.title[lang] }}
                        </li>
                    </ul>
                </td>
            </tr>
            <tr v-if="tagging.research_fields.length > 0">
                <td class="prop-title">{{ trans('research-fields.label') }}</td>
                <td>
                    <ul class="list-ssv">
                        <li v-for="cat in tagging.research_fields" :key="cat.id">
                            {{ cat.title[lang] }}
                        </li>
                    </ul>
                </td>
            </tr>
            <tr v-if="tagging.objectives.length > 0">
                <td class="prop-title">{{ trans('objectives.label') }}</td>
                <td>
                    <ul class="list-ssv">
                        <li v-for="cat in tagging.objectives" :key="cat.id">
                            {{ cat.title[lang] }}
                        </li>
                    </ul>
                </td>
            </tr>
            <tr v-if="tagging.disciplines.length > 0">
                <td class="prop-title">{{ trans('disciplines.label') }}</td>
                <td>
                    <ul class="list-ssv">
                        <li v-for="cat in tagging.disciplines" :key="cat.id">
                            {{ cat.title[lang] }}
                        </li>
                    </ul>
                </td>
            </tr>
            <tr v-if="tagging.topics.length > 0">
                <td class="prop-title">{{ trans('topics.label') }}</td>
                <td>
                    <ul class="list-ssv">
                        <li v-for="cat in tagging.topics" :key="cat.id">
                            {{ cat.title[lang] }}
                        </li>
                    </ul>
                </td>
            </tr>
            <tr v-if="project.content.homepage">
                <td class="prop-title">{{ trans('links.label') }}</td>
                <td>
                    <a :href="project.content.homepage" target="_blank" rel="noopener noreferrer">
                        {{ trans('homepage.label') }}
                    </a>
                </td>
            </tr>
            <tr>
                <td class="prop-title">{{ trans('time_period.label') }}</td>
                <td>
                    <span class="start">{{ partialDate(project.content.start) }}</span>
                    ‒ <span class="end">{{ partialDate(project.content.end) }}</span>
                </td>
            </tr>
            <tr>
                <td class="prop-title">{{ trans('institutions.label') }}</td>
                <td>
                    <ul class="mb-0">
                        <li v-for="inst in project.content.institutions" :key="inst.id">
                            <span class="inst-name">{{ institutionLabel(inst) }}</span>
                            <span class="text-muted text-etc ms-1" v-if="institutionRoles(inst).length > 0">
                                {{ institutionRoles(inst).join(', ') }}
                            </span>
                        </li>
                    </ul>
                </td>
            </tr>
            <tr>
                <td class="prop-title">{{ trans('persons.label') }}</td>
                <td>
                    <ul class="mb-0">
                        <li v-for="person in project.content.persons" :key="person.id" >
                            {{ personLabel(person) }}
                            <span
                                class="text-muted text-etc"
                                v-if="personTags(person, project.content.institutions).length > 0"
                            >
                                {{ personTags(person, project.content.institutions).join(', ') }}
                            </span>
                        </li>
                    </ul>
                </td>
            </tr>
            <tr>
                <td colspan="2" class="big-box">
                    <div class="abstract">
                        <div class="prop-title abstract-title mb-2">{{ trans('abstract.label') }}</div>
                        <div class="abstract-body" v-html="tiptapHTML(abstract)"></div>
                    </div>
                </td>
            </tr>
            <tr v-if="project.content.publications.length > 0">
                <td colspan="2" class="big-box">
                    <div class="abstract">
                        <div class="prop-title abstract-title mb-2">{{ trans('publications.label') }}</div>
                        <p v-for="pub in project.content.publications" :key="pub.id">
                            {{ pub.citation }}
                        </p>
                    </div>
                </td>
            </tr>
        </tbody>
    </table>
</template>

<script>
import { isEmpty } from 'lodash'
import { trans, currentLang } from './AppUtils'
import { generateHTML } from '@tiptap/core'
import DocStarterKit from '@tiptap/starter-kit'
import DocLink from '@tiptap/extension-link'

export default {
    props: {
        // The project model
        project: {
            type: Object,
            default: () => ({id: -1})
        },

        // Category Lookup table (id => {..})
        categories: {
            type: Object,
            default: () => ({}),
        },

        // The category tree
        taxonomy: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            lang: currentLang()
        }
    },
    computed: {
        abstract() {
            const model = this.project.content
            let content = model.abstract[this.lang].content
            if (this.tiptapEmpty(content)) {
                const fallback = (this.lang == 'de') ? 'en' : 'de'
                content = model.abstract[fallback].content
            }
            return content || {type: 'doc', content: []}
        },
        tagging() {
            return {
                disciplines: this.categoryBranch('disciplines'),
                topics: this.categoryBranch('topics'),
                objectives: this.categoryBranch('objectives'),
                research_fields: this.categoryBranch('research-fields'),
                methods: this.categoryBranch('methods'),
            }
        }
    },
    methods: {
        trans,
        institutionsAs(role) {
            const institutions = []
            for (const inst of this.project.content.institutions) {
                const roles = inst.roles
                if (roles.includes(role)) {
                    institutions.push(inst)
                }
            }
            return institutions
        },
        institutionLabel(inst) {
            let label = ""
            if (inst.parent) {
                label += inst.parent + " – "
            }
            label += inst.name
            if (inst.abbr) {
                label += " (" + inst.abbr + ")"
            }
            return label
        },
        institutionRoles(inst) {
            const map = this.trans('institution_roles')
            const roles = []
            for (const r of inst.roles) {
                roles.push(map[r] || r)
            }
            return roles
        },
        personLabel(person) {
            let label = ""
            if (person.title) {
                label += person.title
            }
            if (person.first_name) {
                label += " " + person.first_name
            }
            if (person.last_name) {
                label += " " + person.last_name
            }
            return label.trim()
        },
        // personRoles(person) {
        //     const map = this.trans('person_roles')
        //     const roles = []
        //     for (const r of person.roles) {
        //         roles.push(map[r] || r)
        //     }
        //     return roles
        // },
        personTags(person, institutions) {
            const map = this.trans('person_roles')
            const tags = []
            for (const r of person.roles) {
                tags.push(map[r] || r)
            }
            if (person.institution?.id) {
                const id = person.institution.id
                const inst = institutions.find(i => i.id == id)
                if (inst) {
                    const t = inst.abbr || inst.name
                    if (t) {
                        tags.push(t)
                    }
                }
            }
            return tags
        },
        partialDate(date) {
            let parts = []
            if (date.month) {
                parts.push(date.month)
            }
            if (date.year) {
                parts.push(date.year)
            }
            return (parts.length > 0) ? parts.join("/") : "?"
        },
        categoryBranch(name) {
            const branch = []
            const node = this.taxonomy[name] || {}
            const selected = {}
            for (const [dummy,child] of Object.entries(node.children || {})) {
                selected[child.id] = this.categories[child.id] || {}
            }
            for (const id of this.project.content.taxonomy ?? []) {
                const element = selected[id]
                if (element) {
                    branch.push(element)
                }
            }
            return branch
        },
        tiptapHTML(model) {
            let html = ''
            try {
                html = generateHTML(model, [DocStarterKit, DocLink])
            } catch (error) {
                console.warn("HTML Rendering failed: ", error)
            }
            return html
        },
        tiptapEmpty(model) {
            return isEmpty(model.content || [])
        }
    }
}
</script>
